import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '@environments/environment';

export interface PurchaseInvoice {
    id: number;
    productId?: number;
    companyId?: number | null;
    branchId?: number | null;
    contactId?: number | null;
    no: string;
    date: Date;
    amount: number;
    totalItems: number;
    totalQty?: number;
    soldQty?: number;
    supplierName?: string;
    createdDate?: Date;
    isUnconsumed?: boolean;
    isDeleting?: boolean;
}

export interface PurchaseInvoiceProduct {
    productId: number;
    productName: string;
    barcode?: string;
    articleNo?: string;
    brand?: string;
    sType?: string;
    availableQuantity: number;
    originalQuantity?: number;
    purchaseRate: number;
    discount: number;
    saleRate: number;
    mrp: number;
    tax: number;
    invoiceNo: string;
}

export interface PurchaseInvoiceDetail {
    id: number;
    productId?: number;
    companyId?: number | null;
    branchId?: number | null;
    invoiceNo: string;
    invoiceDate: Date;
    supplierName?: string;
    contactId?: number | null;
    amount: number;
    discount: number;
    cashDiscount: number;
    invoiceTax: number;
    freight?: number;
    insurance?: number;
    items: PurchaseInvoiceProduct[];
}

@Injectable({ providedIn: 'root' })
export class PurchaseInvoiceService {
    constructor(private http: HttpClient) { }

    getUnconsumedInvoices(
        pageNumber: number = 1, 
        pageSize: number = 10, 
        fromDate?: Date,
        toDate?: Date,
        searchTerm?: string,
        companyId?: string, 
        branchId?: string
    ): Observable<any> {
        let params = new HttpParams()
            .set('pageNumber', pageNumber.toString())
            .set('pageSize', pageSize.toString());

        if (fromDate) {
            params = params.set('fromDate', fromDate.toISOString());
        }

        if (toDate) {
            params = params.set('toDate', toDate.toISOString());
        }

        if (searchTerm) {
            params = params.set('searchTerm', searchTerm);
        }

        if (companyId) {
            params = params.set('companyId', companyId);
        }

        if (branchId) {
            params = params.set('branchId', branchId);
        }

        return this.http.get<any>(`${environment.apiUrl}/PurchaseInvoices/unconsumed`, { params });
    }

    getAllInvoices(
        pageNumber: number = 1,
        pageSize: number = 10,
        fromDate?: Date,
        toDate?: Date,
        searchTerm?: string,
        searchDateType?: 'invoiceDate' | 'entryDate',
        companyId?: string,
        branchId?: string,
        sortBy: 'date' | 'createdDate' = 'createdDate',
        sortDirection: 'asc' | 'desc' = 'desc'
    ): Observable<any> {
        let params = new HttpParams()
            .set('pageNumber', pageNumber.toString())
            .set('pageSize', pageSize.toString());

        if (fromDate)    params = params.set('fromDate',   fromDate.toISOString());
        if (toDate)      params = params.set('toDate',     toDate.toISOString());
        if (searchTerm)  params = params.set('searchTerm', searchTerm);
        if (searchDateType) params = params.set('searchDateType', searchDateType);
        if (companyId)   params = params.set('companyId',  companyId);
        if (branchId)    params = params.set('branchId',   branchId);
        params = params.set('sortBy', sortBy);
        params = params.set('sortDirection', sortDirection);

        return this.http.get<any>(`${environment.apiUrl}/PurchaseInvoices/all`, { params });
    }

    getInvoiceByNumber(invoiceNo: string, companyId?: string, branchId?: string, productId?: number): Observable<PurchaseInvoiceDetail> {
        let params = new HttpParams().set('invoiceNo', invoiceNo);

        if (companyId) {
            params = params.set('companyId', companyId);
        }

        if (branchId) {
            params = params.set('branchId', branchId);
        }

        if (productId !== undefined) {
            params = params.set('productId', productId.toString());
        }

        return this.http.get<PurchaseInvoiceDetail>(`${environment.apiUrl}/PurchaseInvoices/by-number`, { params });
    }

    updateInvoice(invoiceNo: string, payload: { contactId?: number | null, discount: number, freight?: number | null, insurance?: number | null, invoiceDate?: Date | null },
                  companyId?: string, branchId?: string, productId?: number): Observable<any> {
        let params = new HttpParams().set('invoiceNo', invoiceNo);
        if (companyId) params = params.set('companyId', companyId);
        if (branchId) params = params.set('branchId', branchId);
        if (productId !== undefined) params = params.set('productId', productId.toString());
        return this.http.patch<any>(`${environment.apiUrl}/PurchaseInvoices/by-number`, payload, { params });
    }

    deleteInvoice(invoiceNo: string, companyId?: string, branchId?: string, productId?: number): Observable<any> {
        let params = new HttpParams().set('invoiceNo', invoiceNo);

        if (companyId) {
            params = params.set('companyId', companyId);
        }

        if (branchId) {
            params = params.set('branchId', branchId);
        }

        if (productId !== undefined) {
            params = params.set('productId', productId.toString());
        }

        return this.http.delete(`${environment.apiUrl}/PurchaseInvoices/by-number`, { params });
    }
}
